import { Link, useElementScrollRestoration } from "@tanstack/react-router";
import { observeElementRect, useVirtualizer } from "@tanstack/react-virtual";
import dayjs from "dayjs";
import React from "react";
import { useObjectRef } from "react-aria";

import { ActiveHoldIndicator, StatIndicator } from "@/components/Indicators";
import { Badge } from "@/components/ui/badge";
import { StatusIcon } from "@/components/ui/icons";
import {
  ListItem,
  ListItemLeft,
  ListItemRight,
} from "@/components/ui/list-item";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { useView } from "@/features/view/components/ViewProvider";
import {
  FocusManagerProvider,
  useFocusManager,
  useFocusManagerItem,
  useFocusManagerState,
  useVirtualNavigation,
} from "@/lib/focus-manager";
import type { TAccession } from "@/lib/types";
import { format } from "@/lib/utils";
import { useNavLocationState } from "@/utils/useNavStack";
import {
  TestPreviewHoverCard,
  TestPreviewProvider,
  useTestOrderPreview,
} from "./TestOrderPreview";

export function AccessionGrid({
  accessions,
  isLoading,
  scrollRef,
  scrollRestorationId,
  selected,
}: {
  accessions: TAccession[];
  isLoading: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  scrollRestorationId: string;
  selected?: string | number | null;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  const { itemRefs, containerProps, ...state } = useFocusManagerState(ref, {
    autoFocus: true,
    initialIndex: accessions.findIndex((acc) => acc.id === selected),
  });

  return (
    <FocusManagerProvider state={state}>
      <InnerGrid
        ref={ref}
        accessions={accessions}
        isLoading={isLoading}
        scrollRef={scrollRef}
        scrollRestorationId={scrollRestorationId}
        selected={selected}
        {...containerProps}
      />
    </FocusManagerProvider>
  );
}

function InnerGrid({
  accessions,
  isLoading,
  scrollRef,
  scrollRestorationId,
  selected,
  ref,
  ...containerProps
}: {
  accessions: TAccession[];
  isLoading: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  scrollRestorationId: string;
  selected?: string | number | null;
} & React.ComponentProps<"div">) {
  const selectedIndex = accessions.findIndex((acc) => acc.id === selected);
  const { getFocusedIndex, setFocusedIndex } = useFocusManager();

  // scroll restoration offset, provided by tanstack router
  const scrollEntry = useElementScrollRestoration({
    id: scrollRestorationId,
  });

  const rowVirtualizer = useVirtualizer({
    count: accessions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 46,
    getItemKey: (index) => accessions[index]?.id,

    observeElementRect(instance, cb) {
      setTimeout(() => {
        if (selectedIndex >= 0) {
          setFocusedIndex(selectedIndex);
        }
      }, 0);
      observeElementRect(instance, cb);
    },
    // scrollPaddingEnd: 48,
    overscan: 10,
    initialOffset: scrollEntry?.scrollY,
  });

  // if data is cached by react-query, the data is available before the scrollRef is mounted
  // this will re-measure the table once the scrollRef is mounted
  React.useEffect(() => {
    if (scrollRef.current) {
      rowVirtualizer.measure();
    }
  }, [scrollRef.current, rowVirtualizer]);

  useVirtualNavigation({
    scrollToIndex: rowVirtualizer.scrollToIndex,
    getFocusedIndex,
    setFocusedIndex,
    items: accessions,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <div
      ref={ref}
      style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      className="@container"
      {...containerProps}
    >
      {virtualItems.map((item, index) => (
        <AccessionGridItem
          item={accessions[item.index]}
          key={item.key}
          index={item.index}
          className="group hover:bg-accent-muted focus-visible:outline-ring/70 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:rounded-md"
          style={{
            height: `${item.size}px`,
            transform: `translateY(${item.start - index * item.size}px)`,
          }}
        />
      ))}
    </div>
  );
}

type AccessionGridItemProps = {
  item: TAccession;
  index: number;
  className?: string;
  style?: React.CSSProperties;
  ref?: React.Ref<HTMLAnchorElement>;
};

function AccessionGridItem(props: AccessionGridItemProps) {
  return (
    <TestPreviewProvider>
      <ItemInner {...props} />
    </TestPreviewProvider>
  );
}

function ItemInner({
  item,
  index,
  ref: anchorRef,
  ...props
}: AccessionGridItemProps) {
  const { settings } = useView();
  const { setOpen } = useTestOrderPreview();
  const ref = useObjectRef(anchorRef);
  useFocusManagerItem(ref, index);
  const { href, label } = useNavLocationState();
  return (
    <ListItem {...props} asChild className={props.className}>
      <Link
        ref={ref}
        to="/accessions/$id"
        params={{ id: item.id }}
        state={{ href, label, selected: item.id }}
        onKeyDown={(e) => {
          if (e.key === " ") {
            e.preventDefault();
            setOpen((flag) => !flag);
          } else {
            setOpen(false);
          }
        }}
      >
        <ListItemLeft>
          <Text muted mono bold size="1">
            {item.id}
          </Text>
          <StatusIcon status={item.status || "new"} />
          <Text truncate>{item.caseId}</Text>
          {item.hasHold && <ActiveHoldIndicator />}
          {item.priority === "STAT" && <StatIndicator />}
        </ListItemLeft>
        <ListItemRight>
          <Badge className="hidden @2xl:inline-flex">{item.org.name}</Badge>
          <TestPreviewHoverCard
            accession={item}
            className="hidden @lg:inline-flex"
          />
          <Text mono muted className="text-xs sm:text-[13px]">
            {settings.dateRelative === "actual"
              ? format(item.date)
              : dayjs(item.date).fromNow()}
          </Text>
        </ListItemRight>
      </Link>
    </ListItem>
  );
}

function AccessionLoadingItem(props: React.ComponentProps<"div">) {
  return (
    <ListItem {...props} className="w-full h-11">
      <ListItemLeft className="gap-3">
        <Skeleton className="w-16" />
        <Skeleton shape="circle" />
        <Skeleton className="w-[70px]" />
      </ListItemLeft>
      <ListItemRight>
        <Skeleton className="w-16 hidden md:block" />
        <Skeleton className="w-20 hidden md:block" />
        <Skeleton className="w-24 md:w-32" />
      </ListItemRight>
    </ListItem>
  );
}
